import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Flex, Modal, Spin, Typography } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { trackButtonClick } from '../../../analytics/mixpanel';
import AppAlert from '../../../CommonComponents/AppAlert/AppAlert';
import ItemCard1 from '../../../CommonComponents/AppCards/ItemCard1/ItemCard1';
import AppButton from '../../../CommonComponents/UI/AppButton/AppButton';
import AppSearchInput from '../../../CommonComponents/UI/AppSearchInput/AppSearchInput';
import { useActionState } from '../../../CommonFuctions/useActionState';
import { formatDateTime } from '../../../CommonFuctions/Utils';
import { usePresets } from '../../Chatbot/usePresets';
import ViewWorkflow from './ViewWorkflow/ViewWorkflow';
import styles from './Workflow.module.scss';

const { Title, Paragraph, Text } = Typography;
const ACTION_STATE = {
  deleteConfirmModal: 'deleteConfirmModal',
  workflowDetailModal: 'workflowDetailModal',
};

const Workflow = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const { actionState, setCurrentActionState, resetActionState } = useActionState();
  const { workflows, presetsLoading, presetsError, deletePreset, deletePresetLoading } =
    usePresets();

  const handleCreateWorkflow = () => {
    navigate('create');
  };

  const handleViewWorkflow = (preset) => {
    setCurrentActionState({
      type: ACTION_STATE.workflowDetailModal,
      data: preset,
    });
  };

  const handleEditWorkflow = (id) => {
    navigate(`edit/${id}`);
  };

  const handleDeleteWorkflow = (preset) => {
    setCurrentActionState({
      type: ACTION_STATE.deleteConfirmModal,
      data: preset,
    });
  };

  const handleConfirmDelete = async () => {
    const preset = actionState.data;
    try {
      trackButtonClick({ type: 'Workflow - Delete Workflow' });
      await deletePreset(preset.preset_id);
      resetActionState();
    } catch (error) {
      // Error handling is done in usePresets hook
    }
  };

  const filteredWorkflows = workflows.filter(
    (preset) =>
      preset.preset_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (preset.preset_description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title level={2} className={styles.title}>
          Workflows
        </Title>
        <Paragraph className={styles.description}>
          Create and save custom extraction prompts as workflows to extract required data from one
          or multiple documents uploaded.
        </Paragraph>
        <Flex gap='large'>
          <AppSearchInput
            placeholder='Search Workflows'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchBar}
          />
          <AppButton
            variant='outlined'
            $borderRadius='8px'
            color='primary'
            icon={<PlusOutlined />}
            onClick={handleCreateWorkflow}
          >
            Create a New Workflow
          </AppButton>
        </Flex>
      </div>

      {presetsLoading && (
        <div className={styles.loaderContainer}>
          <Spin size='large' indicator={<LoadingOutlined />} />
        </div>
      )}

      {!presetsLoading && presetsError && <Text type='danger'>{presetsError.message}</Text>}

      {!presetsLoading && !presetsError && (
        <div className={styles.cardsContainer}>
          {filteredWorkflows.map((preset) => (
            <ItemCard1
              key={preset.preset_id}
              title={preset.preset_name}
              description={preset.preset_description}
              dateTime={formatDateTime(preset.updated_at)}
              onEdit={() => handleEditWorkflow(preset.preset_id)}
              onDelete={() => handleDeleteWorkflow(preset)}
              onClick={() => {
                trackButtonClick({ type: 'View Workflow' });
                handleViewWorkflow(preset);
              }}
            />
          ))}
          {filteredWorkflows.length === 0 && searchTerm && (
            <Typography.Text type='secondary' className={styles.noResults}>
              No workflows found matching &quot;
              {searchTerm}
              &quot;
            </Typography.Text>
          )}
        </div>
      )}

      <Modal
        title={<Title level={4}>Delete workflow</Title>}
        open={actionState.type === ACTION_STATE.deleteConfirmModal}
        footer={null}
        onCancel={resetActionState}
      >
        <Paragraph className='mb-8'>
          Are you sure you want to delete workflow{' '}
          <b>
            &quot;
            {actionState.data?.preset_name}
            &quot;
          </b>
          ?
        </Paragraph>
        <AppAlert
          message='Deleting this workflow will delete previously generated presets.'
          type='warning'
        />
        <Flex justify='flex-end' gap={12} className='mt-24'>
          <AppButton onClick={resetActionState}>Cancel</AppButton>
          <AppButton
            danger
            type='primary'
            loading={deletePresetLoading}
            onClick={handleConfirmDelete}
          >
            Delete
          </AppButton>
        </Flex>
      </Modal>
      <ViewWorkflow
        isOpen={actionState.type === ACTION_STATE.workflowDetailModal}
        data={actionState.data}
        onClose={resetActionState}
      />
    </div>
  );
};

export default Workflow;
